import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { apiService } from '../services/apiService';
import { detectBrowserTimezone } from '../utils/timezoneUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeAuth();
  }, []);

  // Phase 2A: Background timezone detection after login
  useEffect(() => {
    const detectAndSaveTimezone = async () => {
      if (!user) return;

      try {
        // Detect browser timezone (instant, non-blocking)
        const detectedTimezone = detectBrowserTimezone();
        console.log('🌍 [TIMEZONE] Auto-detected:', detectedTimezone);

        // Send to backend in background (fire-and-forget)
        apiService.updateUserTimezone(detectedTimezone)
          .then(() => {
            console.log('✅ [TIMEZONE] Saved successfully:', detectedTimezone);
          })
          .catch((error) => {
            // Silent fail - user already logged in, not critical
            console.warn('⚠️  [TIMEZONE] Failed to save (non-critical):', error.message);
          });
      } catch (error) {
        // Fallback to UTC - no problem!
        console.warn('⚠️  [TIMEZONE] Detection failed, using UTC:', error);
      }
    };

    // Run timezone detection after successful login
    if (user) {
      detectAndSaveTimezone();
    }
  }, [user]);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      
      // Initialize auth service
      await authService.initialize();
      
      // Check if user is already logged in
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        // Ensure we have the complete user object
        setUser({
          ...currentUser,
          displayName: currentUser.displayName || currentUser.name || 'User',
          photoURL: currentUser.avatar_url || currentUser.photoURL
        });
      }

      // Listen for auth state changes
      window.addEventListener('authStateChanged', handleAuthStateChange);

    } catch (err) {
      setError('Failed to initialize authentication');
      console.error('Auth initialization error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthStateChange = (event) => {
    const userData = event.detail.user;
    if (userData) {
      setUser({
        ...userData,
        displayName: userData.displayName || userData.name || 'User',
        photoURL: userData.avatar_url || userData.photoURL
      });
    } else {
      setUser(null);
    }
    setError(null);
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const userData = await authService.signInWithGoogle();
      setUser({
        ...userData,
        displayName: userData.displayName || userData.name || 'User',
        photoURL: userData.avatar_url || userData.photoURL
      });
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = async (updates) => {
    try {
      // Update local state
      setUser(prev => ({
        ...prev,
        ...updates
      }));
      
      // Update localStorage
      const storedUser = JSON.parse(localStorage.getItem('sarvik_user') || '{}');
      const updatedUser = { ...storedUser, ...updates };
      localStorage.setItem('sarvik_user', JSON.stringify(updatedUser));
      
      // TODO: Call backend API to update user profile
      // await authService.updateProfile(updates);
      
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const signOut = async () => {
    setLoading(true);
    
    try {
      // Clear all session storage items for this user
      if (user) {
        const userId = user.uid || user.id;
        // Clear session markers
        sessionStorage.removeItem(`current_session_${userId}`);
        sessionStorage.removeItem(`voice_prompt_shown_${userId}_${new Date().toDateString()}`);
        sessionStorage.removeItem(`login_session_${userId}`);
        sessionStorage.removeItem(`toast_shown_session_${userId}`);
        
        // Clear any other session data
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.includes(userId)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
      }
      
      await authService.signOut();
      setUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    signInWithGoogle,
    signOut,
    updateUserProfile,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
